import {
	FC, forwardRef, useCallback, useMemo,
} from 'react';
import { getTeamAccessEntities } from 'wb-reddit/app/routes/teams/utils';
import RedditAccessImages from 'wb-reddit/app/routes/reports/components/accessImages';
import {
	TrashCanIcon,
	Button,
	ButtonAppearance,
	ButtonColor,
	isReddit,
	Tooltip,
	useOpen,
	DeleteDialog,
} from '@aleph/shared-components';
import { useMutation } from '@apollo/client';
import {
	DeleteUserGroupDocument, RedditUserGroupsListDocument, UserGroupRole,
} from 'wb-reddit/graphql/generated';
import useRedditTeamMutationStore from 'wb-reddit/app/routes/teams/hooks/useRedditTeamMutationStore';
import AccessBadge from 'wb-reddit/app/common/components/accessBadge';
import { RedditAccessEntity } from 'wb-reddit/app/routes/reports/utils';
import { AgencyAccountType, AuthAgencyAccountType } from 'wb-reddit/app/routes/advertisers/utils';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import EntityName from 'wb-reddit/app/common/components/EntityName';
import { HandleUpdateTeamRow, TeamRow } from 'wb-reddit/app/routes/teams/container';
import { useTranslation } from 'react-i18next';
import {
	UserGroup,
	UserGroupAccessRole,
	UserAccountLevel as AuthLevel,
	AuthGraphDeleteUserGroupDocument,
	AuthGraphUserGroupsListDocument,
} from 'wb-spotify/graphql/authgraph/generated';
import useSpotifyRolePermissions from 'wb-spotify/app/common/hooks/useSpotifyPermissions';

interface IProps {
	group: TeamRow | UserGroup;
	advertisers: AgencyAccountType[] | AuthAgencyAccountType[];
	deleteRow: HandleUpdateTeamRow
}

const UserGroupItem: FC<IProps> = forwardRef<HTMLDivElement, IProps>(
	({
		group, advertisers, deleteRow, ...rest
	}, ref) => {
		const { t } = useTranslation();
		const [deleteGroup, { loading: deleting }] = useMutation(isReddit() ? DeleteUserGroupDocument : AuthGraphDeleteUserGroupDocument, {
			refetchQueries:      [isReddit() ? RedditUserGroupsListDocument : AuthGraphUserGroupsListDocument],
			awaitRefetchQueries: true,
		});
		const [deleteOpen, { onOpen: openDelete, onClose: closeDelete }] = useOpen(false);

		const { setMutatingTeam } = useRedditTeamMutationStore();

		const owner = isReddit()
			? (group as TeamRow).users?.find(user => user.userGroupUserRole === UserGroupRole.Owner)?.user
			: (group as UserGroup).userGroupAccess?.find(item => item?.userGroupAccessRole === UserGroupAccessRole.Owner)?.user;

		const admins = useMemo(
			() => (isReddit()
				? [
					owner,
					...(group as TeamRow).users
						?.filter(user => user.userGroupUserRole === UserGroupRole.Admin)
						?.map(user => user.user) ?? [],
				]
				: [...((group as UserGroup)?.userGroupAccess ?? [])]
					?.filter(item => (
						item?.userGroupAccessRole
							&& (
								[UserGroupAccessRole.Admin, UserGroupAccessRole.Owner]
									.includes(item.userGroupAccessRole)
							)
					))
					?.sort((a, b) => {
						if (a?.userGroupAccessRole === UserGroupAccessRole.Owner) return -1;
						if (b?.userGroupAccessRole === UserGroupAccessRole.Owner) return 1;

						return a?.userGroupAccessRole?.localeCompare(b?.userGroupAccessRole as string) ?? 0;
					})
					?.map(item => item?.user)),
			[group],
		);

		const members = useMemo(() => (isReddit()
			? ((group as TeamRow)?.users?.map(user => user.user) ?? []) as RedditAccessEntity[]
			: [...((group as UserGroup)?.userGroupAccess ?? [])]
				?.map(item => item?.user)), [group]) as RedditAccessEntity[];

		const agencyAccounts = useMemo(() => (isReddit()
			? ((group as TeamRow)?.agencyAccounts
				?.map(agencyAccount => advertisers
					// @ts-ignore
					?.find((advertiser) => advertiser.id === agencyAccount.id)) || []) as RedditAccessEntity[]
			: [...((group as UserGroup)?.advertiserAccountAccess ?? [])]
				?.map(item => item?.advertiserAccount)), [group, advertisers]) as RedditAccessEntity[];

		const accessEntities = getTeamAccessEntities(group as TeamRow) || [];
		const platformUser = useUserMetaData();

		const salesforceId = (group as TeamRow)?.salesforceId;
		const groupId = isReddit()
			? (group as TeamRow).id
			: (group as UserGroup).userGroupId;

		function onEditTeam() {
			setMutatingTeam(group as TeamRow);
		}

		const { canDeleteTeam: spotifyCanDeleteTeam, canEditTeam: spotifyCanEditTeam } = useSpotifyRolePermissions();

		const canDeleteTeam = useMemo(() => {
			// On reddit only admin can delete teams
			if (isReddit() && platformUser?.isAdmin) {
				return true;
			}

			// On spotify app the userAccountLevel is of type AuthLevel, platform admins can delete any teams
			if ((platformUser?.userAccountLevel as unknown as AuthLevel) === AuthLevel.PlatformAdmin) {
				return true;
			}

			// On spotify  if correct role and is owner of the team
			return spotifyCanDeleteTeam && platformUser?.userId === owner?.userId;
		}, [platformUser, spotifyCanDeleteTeam]);

		const canEditTeam = useMemo(() => {
			if (isReddit()) {
				return platformUser?.isAdmin;
			}

			return spotifyCanEditTeam;
		}, [platformUser, spotifyCanEditTeam]);

		const deleteCallback = useCallback(async () => {
			await deleteGroup({
				variables: {
					userGroupId: group.userGroupId,
				},
			});
		}, [group]);

		return (
			<div
				className="flex h-[82px] border-b border-b-200 items-center"
				{...rest}
				ref={ref}
				data-testid={`team-${groupId}`}
			>
				<DeleteDialog
					isOpen={deleteOpen}
					onClose={closeDelete}
					row={group as TeamRow}
					deleteRow={deleteRow}
					deleteApi={deleteCallback}
					entityName={group.userGroupName}
				/>
				<EntityName
					name={group.userGroupName}
					// TODO provide type with mutations
					onClick={canEditTeam ? onEditTeam : undefined}
					entity={group}
					classname="whitespace-nowrap min-w-[250px]"
				/>
				<Tooltip
					content={admins.map(admin => (
						<div className="flex gap-[8px] items-center">
							{admin?.userName}
						</div>
					))}
				>
					<div className="gap-[8px] px-[8px] w-[203px] items-center text-gray-950 whitespace-nowrap overflow-hidden overflow-ellipsis">
						{admins.map(admin => (admin?.userName)).join(', ')}
					</div>
				</Tooltip>
				<div className="px-[8px] w-[172px]">
					<RedditAccessImages
						accessEntities={members}
						width={158}
					/>
				</div>
				<div className="px-[8px] w-[162px]">
					<RedditAccessImages
						accessEntities={agencyAccounts}
						width={158}
					/>
				</div>
				<div className="flex px-[8px] pt-[17px] pb-[18px] w-[128px]">
					<AccessBadge
						accessEntities={accessEntities}
						label="Grid view"
						labelClassName='min-w-[68px] text-[12px]'
						// TODO provide for spotify as well
						href={`/superGrid/(filters:(userGroupId:((value:(${groupId})))))`}
					/>
				</div>
				<div className="px-[8px] w-[100px]">
					{salesforceId ? <a
						href={`https://alephholding.my.salesforce.com/${salesforceId}`}
						target="_blank"
						className="text-blue hover:text-blue-600"
						rel="noreferrer"
					>
						{t('re: advertiser.open_profile', 'Open profile')}
					</a> : <span className="text-gray-600 cursor-disabled">{t('re: advertiser.open_profile', 'Open profile')}</span>
					}
				</div>
				<div className="w-[70px] flex items-center justify-center">
					{canDeleteTeam && (
						<Button
							leftNode={<TrashCanIcon />}
							appearance={ButtonAppearance.GHOST}
							loading={deleting}
							disabled={deleting}
							color={ButtonColor.RED}
							onClick={() => openDelete()}
						/>
					)
					}
				</div>
			</div>
		);
	},
);

export default UserGroupItem;
